/** Angular */
import { Injectable } from '@angular/core';
/** Rxjs */
import { Observable } from 'rxjs';
/** Services */
import { BydRhsAbstractService } from '../../../../shared/services/byd-rhs-abstract.service';
/** Models */
import { DsLinearWorkModel } from '../../models/ds-linear-work-model';
/** Sixense */
import { BydModel } from '@sixense/byd-core-service';
/** Interfaces */
import { DsOrganisationLinearWorkFilterInterface } from '../../interfaces/ds-organisation-linear-work-filter.interface';
import { DsLinearWorkFormOrderInterface } from '../../../ds-linear/interfaces/ds-linear-work-form-order.interface';
/** Functions */
import { BydGenerateParams } from '../../../../functions/byd-generate-params.function';

@Injectable({
  providedIn: 'root',
})
export class DsOrganisationLinearWorkModelsService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get linear work models of the organisation.
   *
   * @param organisationId Organisation ID
   * @param filter Search filter
   * @param elementsPerPage Elements per page
   * @param pageIndex Page index
   * @returns Observable type `<BydModel<DsLinearWorkModel[]>>`
   */
  public getLinearWorkModels(
    organisationId: number,
    filter?: DsOrganisationLinearWorkFilterInterface,
    elementsPerPage = 10,
    pageIndex = 0
  ): Observable<BydModel<DsLinearWorkModel>> {
    this.path = `/v1/organisations/${organisationId}/works`;
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    const params = BydGenerateParams<DsOrganisationLinearWorkFilterInterface>(filter);
    return this.getModels<DsLinearWorkModel>({ headers, params });
  }

  /**
   * Get selected linear work model.
   *
   * @param organisationId Organisation ID
   * @param linearWorkModelId Id of the linear work model to get
   * @returns Observable type `<BydModel<DsLinearWorkModel>>`
   */
  public getLinearWorkModel(organisationId: number, linearWorkModelId: number): Observable<DsLinearWorkModel> {
    this.path = `/v1/organisations/${organisationId}/works/${linearWorkModelId}`;
    return this.getModel<DsLinearWorkModel>();
  }

  /**
   * Add a new linear work model for the organisation.
   *
   * @param organisationId Organisation ID
   * @param name Name of the model to create
   * @returns Observable<number>
   */
  public addLinearWorkModel(organisationId: number, name: string): Observable<number> {
    this.path = `/v1/organisations/${organisationId}/works`;
    return this.post<number>({ body: { name } });
  }

  /**
   * Rename a linear work model.
   *
   * @param organisationId Organisation ID
   * @param linearWorkModelId Id of the linear work model
   * @param name New name
   * @returns Observable<boolean>
   */
  public renameLinearWorkModel(organisationId: number, linearWorkModelId: number, name: string): Observable<boolean> {
    this.path = `/v1/organisations/${organisationId}/works/${linearWorkModelId}`;
    return this.put<boolean>({ body: { name } });
  }

  /**
   * Updates the linked form templates and order for the current work model.
   *
   * @param organisationId Organisation ID
   * @param linearWorkModelId Id of the linear work model
   * @param formInfos List of form ids with their order
   * @returns Observable<boolean>
   */
  public updateLinkedForms(
    organisationId: number,
    linearWorkModelId: number,
    formInfos: DsLinearWorkFormOrderInterface[]
  ): Observable<boolean> {
    this.path = `/v1/organisations/${organisationId}/works/${linearWorkModelId}`;
    return this.post<boolean>({ body: { formInfos } });
  }

  /**
   * Delete the selected linear work model.
   *
   * @param organisationId Organisation ID
   * @param linearWorkModelId Id of the linear work model to delete
   * @returns Observable<boolean>
   */
  public deleteLinearWorkModel(organisationId: number, linearWorkModelId: number): Observable<boolean> {
    this.path = `/v1/organisations/${organisationId}/works/${linearWorkModelId}`;
    return this.delete<boolean>();
  }

  /**
   * Create DsLinearWorkModel instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsLinearWorkModel` instance.
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public createNewInstance(jsonObject: any): DsLinearWorkModel {
    return new DsLinearWorkModel(jsonObject);
  }
}
